import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import './index.scss'
import { BASE_COLUMNS, MAINTENANCE_DATA } from './data'
import Icon from '../../../components/Icon'
import { COLOR } from '../../../data/variables'
import Button, { Button as ButtonType } from '../../../components/Button'
import { useTranslation } from 'react-i18next'
import { ICON } from '../../../components/Icon/data'
import Table from '../../../components/Table'
import { INSTANCE_COL_IDS } from './table/data'
import { useModal } from '../../../hooks/useModal'
import AddItemForm from '../../../components/AddItemForm'
import InstanceTitle from './table/InstanceTitle'
import ServiceColumn from './table/ServiceColumn'
import { useToast } from '../../../hooks/useToast'
import { DTO_MaintenanceBase, DTO_MaintenanceMobile, DTO_MaintenanceRecord, DTO_ServiceBase, Entity_ServiceBase } from '../../../services/calls/types/maintenance'
import InstanceColumn from './table/InstanceColumn'
import { useFormat } from '../../../hooks/useFormat'
import { getServiceInstance, removeServiceBase } from '../../../services/calls/maintenance'
import { saveServiceBase } from './utils'
import { ActionResponse } from '../../../components/AddItemForm/types'
import { SINGLE_BASE } from '../CarMaintenance'
import { DateTime } from 'luxon'

type MaintenanceInstance = {
  data: DTO_MaintenanceMobile | DTO_MaintenanceMobile[]
  type: 'milage' | 'date' | 'single'
  mobile: DTO_MaintenanceBase
  bases?: Record<number, DTO_ServiceBase>
  refetch?: () => void
  refetchBases?: () => void
  isLoading?: boolean
}

const SCROLL_OFFSET = -4

const MaintenanceInstance = memo(({
  type, data, mobile, bases, refetch = () => {}, refetchBases = () => {}, isLoading = false
}: MaintenanceInstance) => {
  const { t } = useTranslation()
  const modalRef = useRef()
  const { openModal, closeModal } = useModal()
  const { addToast } = useToast()
  const { localizeNumbers } = useFormat()

  const instanceInfo = useMemo(() => MAINTENANCE_DATA[type] ?? {}, [type])

  const deleteAction = useCallback(async (id: number, isBase: boolean) => {
    try {
      let resp: ActionResponse
      if (isBase) resp = await removeServiceBase(id)
      else resp = await instanceInfo.deleteFunction(id)

      if (resp.status === 'success') {
        await refetch()
        await refetchBases()
        closeModal()
      } else {
        modalRef?.current?.setError(resp.error)
      }
    } catch (err: any) {
      if (err.response?.data) {
        addToast({
          status: "error",
          type: "toast",
          message: err.response.data?.detail ?? t('An error came up with your delete request')
        })
      }
    }
  }, [instanceInfo, addToast, closeModal, refetch, refetchBases])

  const saveAction = useCallback(async (isBase: boolean) => {
    try {
      const newData = modalRef?.current?.getData()
      let resp: ActionResponse
      if (isBase) resp = await saveServiceBase(newData, newData.id)
      else {
        if (type === 'single') {
          let singleBase = Object.values(bases ?? {}).find((base) => base.description === SINGLE_BASE)
          if (!singleBase) { // If no base, we need to create it first
            const newBase: Entity_ServiceBase = {
              mobile: mobile.id,
              description: SINGLE_BASE,
              no_repeat: true,
              enabled: true,
              comment: 'Single instance'
            }
            const baseResp = await saveServiceBase(newBase)
            singleBase = baseResp.data
          }

          const id = newData?.[instanceInfo.idKey]
          const oldData = data.find((d) => d.id === id)
          resp = await instanceInfo.saveFunction({ ...newData, service: singleBase.id }, id, oldData)
        } else {
          resp = await instanceInfo.saveFunction(newData, newData?.[instanceInfo.idKey], data)        }
      }

      if (resp.status === 'success') {
        await refetchBases()
        await refetch()
        closeModal()
      } else {
        modalRef?.current?.setError(resp.error)
      }
    } catch (err: any) {
      const { data } = err?.response
      addToast({
        status: "error",
        type: "toast",
        message: data?.detail ?? data?.non_field_errors ?? t('An error came up with your save request')
      })
    }
  }, [
    instanceInfo, modalRef.current, data, closeModal, addToast, refetch, refetchBases, bases, type
  ])

  const openAddModal = useCallback(async (data: any, isBase: boolean, instanceId?: number) => {
    let useData = { ...data }
    if (instanceId) {
      const resp = await getServiceInstance(instanceId)
      if (resp) useData = resp
    }

    const title = {
      single: data ? t('Edit single instance') : t('Add single instance'),
      milage: data ? t('Edit check schedule based on milage') : t('New check schedule based on milage'),
      date: data ? t('Edit check schedule based on date') : t('New check schedule based on date'),
    }
    
    const modalTitle = (
      <div className="ModalHeader">
        <div className="ModalHeader__text">
          <div className="ModalHeader__title">{title[type]}</div>
          <div className="ModalHeader__subtitle">{`${t('Vehicle')} ${mobile.globalid}`}</div>
        </div>
      </div>
    )

    let formatedData = {}
    if (isBase) {
      formatedData = {
        ...useData,
        type,
        mobile: mobile.id,
        estimated_duration: data?.estimated_duration ? data?.estimated_duration / 3600 : null, // seconds to hours
        add_interval: data?.interval_day && data?.interval_day !== 0
      }

      if (data?.first) formatedData.first = data.first / 1000 //meters to km
      if (data?.interval) formatedData.interval = data.interval / 1000 // meters to km
    } else {
      formatedData = {
        ...useData,
        service: useData.servicebase_id,
        mobile: mobile.id,
        actual_value: useData?.actual_value / 1000 // meters to km
      }
    }

    openModal({
      header: modalTitle,
      content: (
        <AddItemForm
          ref={modalRef}
          default_data={formatedData}
          inputs={isBase ? BASE_COLUMNS : instanceInfo.modalFields}
        />
      ),
      customButtons: (
        <div className="MaintenanceInstance__modal-buttons">
          {data?.[instanceInfo.idKey] && (
            <Button
              size="l"
              type="tertiary"
              label={(
                <>
                  {t('Delete this entry')}
                  <Icon icon={ICON.TRASH} />
                </>
              )}
              onClick={() => deleteAction(formatedData.id, isBase)}
            />
          )}
          <div className="MaintenanceInstance__modal-buttons__right">
            <Button
              label={t('Cancel')}
              onClick={() => closeModal()}
              type="tertiary"
            />
            <Button
              label={t('Save')}
              onClick={() => saveAction(isBase)}
            />
          </div>
        </div>
      )
    })
  }, [type, t, openModal, mobile, modalRef, instanceInfo])

  const addButton = useMemo(() => {
    const gridParams: ButtonType = {
      type: type === 'single' ? 'tertiary' : 'primary',
      size: 'm',
      label: (
        <>
          {type === 'single' ? t('New single instance') : t('New schedule')}
          <Icon icon={ICON.ADD} color={type === 'single' ? undefined : COLOR.WHITE} />
        </>
      ),
      onClick: () => openAddModal(undefined, type !== 'single')
    }

    const tableParams: ButtonType = {
      ...gridParams,
      type: 'primary',
      label: (
        <>
          {type === 'single' ? t('New single instance') : t('New schedule')}
          <Icon icon={ICON.ADD} color={COLOR.WHITE} />
        </>
      )
    }
    return { grid: <Button { ...gridParams } />, table: <Button { ...tableParams} /> }
  }, [type, t, openAddModal])

  const emptyElem = useMemo(() => {
    const { label, description, icon } = instanceInfo
    return (
      <div className="MaintenanceInstance__empty">
        <div className="MaintenanceInstance__empty-content">
          <Icon icon={icon} color={COLOR.RED_80} />
          <div className="MaintenanceInstance__empty-label">{t(label)}</div>
          <div className="MaintenanceInstance__empty-subtitle">{t(description)}</div>
        </div>
        {addButton.grid}
      </div>
    )
  }, [instanceInfo, t, type, addButton])

  const records = useMemo(() => {
    if (type !== 'single') return data?.records ?? []
    return data ?? []
  }, [type, data])

  const formatFunc = useCallback((id: string, val: any, row: any) => {
    if (id === INSTANCE_COL_IDS.COST || id === INSTANCE_COL_IDS.ODOMETER) {
      return val?.toLocaleString() ?? ''
    } else if (id === INSTANCE_COL_IDS.SERVICE) {
      return (
        <ServiceColumn label={val} onClick={() => openAddModal(row, false, val.id)} />
      )
    } else if (id === INSTANCE_COL_IDS.KM_INSTANCE || id === INSTANCE_COL_IDS.MONTH_INSTANCE) {
      return (<InstanceColumn value={val} row={row} openModal={() => openAddModal(val, false, val.instance_id)} />)
    } else if (id === INSTANCE_COL_IDS.KM || id === INSTANCE_COL_IDS.MONTH) {
      const details = row.base
      return (
        <div
          className="MaintenanceInstance__clickable"
          role="button"
          tabIndex={0}
          onClick={() => openAddModal(details, true)}
        >
          {val}
        </div>
      )
    }
    return val
  }, [openAddModal, openModal])

  const headerFormatFunc = useCallback((id: string, val: string, row: any) => {
    if (id === INSTANCE_COL_IDS.KM_INSTANCE) {
      return localizeNumbers(Number(val), 0)
    } else if (id === INSTANCE_COL_IDS.MONTH_INSTANCE) {
      const month = DateTime.fromFormat(val, 'yyyy-LL')
      return month.isValid ? month.toFormat('LLLL') : val
    } else if (id === INSTANCE_COL_IDS.KM || id === INSTANCE_COL_IDS.MONTH) {
      return (
        <div className="MainCol">
          <Icon icon={id === INSTANCE_COL_IDS.KM ? ICON.METER : ICON.CALENDAR} />
          {val}
        </div>
      )
    }

    return val
  }, [])

  const closestHeader = useMemo(() => {
    const currValue = type === 'milage' ? mobile.odometer : DateTime.now().toFormat('yyyy-LL')
    return data?.headers?.filter((key) => key >= currValue)
  }, [data, mobile, type])

  const mappedHeader = useMemo(() => {
    const lookup = {}
    if (instanceInfo?.tableColumns) {
      let colRow = 1
      instanceInfo?.tableColumns.forEach((col) => {
        const { id, isDynamic } = col

        if (isDynamic) {
          data?.headers?.forEach((key, idx) => {
            const classes = ['dynamic']
            if (closestHeader?.[0] === key) classes.push('next')
            if (!closestHeader.includes(key)) classes.push('past')
            if (idx % 2 === 0) classes.push('tiger')

            lookup[key] = {
              ...col,
              disableSort: true,
              index: colRow,
              label: key,
              class: classes,
              formatFunction: (val, row) => formatFunc(id, val, row),
              headerFormatFunction: (val, row) => headerFormatFunc(id, val, row)
            }
            colRow = colRow + 1
          })
        } else {
          lookup[id] = {
            ...col,
            index: colRow,
            formatFunction: (val, row) => formatFunc(id, val, row),
            headerFormatFunction: (val, row) => headerFormatFunc(id, val, row)
          }
          colRow = colRow + 1
        }
      })
    }
    return lookup
  }, [
    instanceInfo.tableColumns, data, openAddModal, mobile, formatFunc, headerFormatFunc,
    closestHeader
  ])

  const baseRecords = useMemo(() => {
    const newBases: Record<number, DTO_ServiceBase & { records?: DTO_MaintenanceRecord[] }> = { ...bases }
    if (records.length) {
      records.forEach((record: DTO_MaintenanceRecord) => {
        if (newBases[record.servicebase_id]) {
          if (!newBases[record.servicebase_id].records) newBases[record.servicebase_id].records = []
          newBases[record.servicebase_id].records.push(record)
        }
      })
    }
    return newBases
  }, [bases, records])

  const rows = useMemo(() => {
    const rowArr = []
    if (Object.keys(baseRecords ?? {}).length && type !== 'single') {
      Object.values(baseRecords ?? {}).forEach((base) => {
        if (!base.enabled) return
        const newRow: Record<string, any> = {}
        base.records?.forEach((d) => {
          Object.keys(mappedHeader).forEach((id) => {
            const { path, isDynamic } = mappedHeader[id]

            if (isDynamic) {
              if (d.header_value.toString() === id) newRow[id] = d
            } else {
              const value = d[path]
              newRow[id] = value
            }
          })
        })
        const hasDynamicData = Object.keys(newRow).some((id) => mappedHeader[id]?.isDynamic && newRow[id] !== undefined)
        if (hasDynamicData) rowArr.push({ base, ...newRow })
      })
    } else if (records.length && type === 'single') {
      records.forEach((d) => {
        const newRow = { ...d }
        Object.keys(mappedHeader).forEach((id) => {
          const { path } = mappedHeader[id]
          let value = d[path]
          if (id === INSTANCE_COL_IDS.ODOMETER) value = value / 1000 // meters to km
          newRow[id] = value
        })
        rowArr.push(newRow)
      })
    }
    return rowArr
  }, [mappedHeader, baseRecords, records, type])

  const scrollToColumn = useCallback((index: number) => {
    if (type !== 'single') {
      const tableId = `table_${type}`
      const container = document.getElementById(`${tableId}_container`)
      const table = document.getElementById(tableId)
      const firstRow = table?.rows[0]
      if (firstRow && firstRow.cells[index]) {
        const targetCell = firstRow.cells[index]
        const leftOffset = targetCell.offsetLeft
    
        container?.scrollTo({
          left: leftOffset,
          behavior: 'smooth'
        });
      }
    }
  }, [type])

  useEffect(() => {
    if (rows.length && closestHeader?.length) {
      const closestKey = closestHeader[0]
      const { index } = mappedHeader?.[closestKey] ?? 0
      scrollToColumn(index + SCROLL_OFFSET)
    }
  }, [rows])

  const rowHeight = useMemo(() => {
    switch (type) {
      case 'milage':
        return 36
        break
      case 'date':
        return 46
        break
      default:
        return undefined
    }
  }, [type])

  const tableElem = useMemo(() => {
    return (
      <div className="MaintenanceInstance__table">
        <Table
          isLoading={isLoading}
          tableId={`table_${type}`}
          headerElems={[addButton.table]}
          showSearch={false}
          tableHeader={mappedHeader}
          rowHeight={rowHeight}
          headerRowHeight={35}
          rows={rows}
          includePageFooter={type === 'single'}
          title={
            <InstanceTitle
              instanceInfo={instanceInfo}
              mobile={mobile}
            />
          }
        />
      </div>
    )
  }, [instanceInfo, mappedHeader, rows])

  return (
    <div
      className="MaintenanceInstance"
      style={{ width: records.length ? '100%' : undefined }}
    >
      {!records?.length ? emptyElem : tableElem}
    </div>
  )
})

export default MaintenanceInstance
